import { Injectable, OnDestroy, inject, signal } from '@angular/core';
import { Auth } from '@angular/fire/auth';
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';
import { Store } from '@ngrx/store';
import { map, tap, takeUntil, take } from 'rxjs/operators';

import { IUser } from 'src/app/features/user-access/models/user/user';
import { SharedModalUtils } from 'src/app/shared/shared-utils/shared-modal/shared-modal.utils';
import { ISharedModalBasic } from 'src/app/shared/shared-models/modals/shared-modal-basic';
import {
  IconColourEnum,

} from 'src/app/shared-modules/shared-icons/models/icons-colour.enum';
import { CollectionUsersSettingsService } from 'src/app/shared/shared-services/firebase/collection-users-settings.service';
import { path_users_userId_settings_stores_storeId_mailer_settings } from 'src/app/shared/shared-services/database-paths';
import {settingUserStoreMailerConfiguration} from 'src/app/features/settings-new/store/setting.actions';
import {getColleagues} from 'src/app/features/core/store-shared/shared.actions';
import {selectColleagues} from 'src/app/features/core/store-shared/shared.selectors';
import {IColleagues} from 'src/app/features/user-access/models';
import { IGlobalError } from 'src/app/shared/shared-components/components/global-error-display/models/global-error';
import { setGlobalErrorOccurrence } from 'src/app/features/notifications/store/notification.actions';
import { ErrorName } from 'src/app/shared/shared-components/components/global-error-display/models/global-error.enum';

import { IEmailSettingsFormState, IFormErrors } from '../models';
import { EmailValidationManager } from './email-validation.manager';
import { EmailConfigurationManager } from './email-configuration.manager';
import { defaultEmailSettings, initialState } from '../constants';
import { isNonEmptyObject } from '../../../../../shared/shared-utils/object/object.utils';
import {IconsEnum} from '../../../../../shared-modules/shared-icons/models/icons.enum';
import {IEmailSettings} from '../../../models';
import {selectEmailSettingsView} from '../../../store/setting.selectors';
import {
  applyDisconnectedAccount,
  isMicrosoft365Auth,
  MAIL_AUTH_MICROSOFT_365,
  MAIL_AUTH_SMTP,
  microsoftMailboxAddress,
  shouldPersistStandardAuthType,
  TMailAuthType,
  toPersistableMailerSettings,
} from '../../../models/email/microsoft-mail.utils';
import {ISelectOption} from '../../../../../shared/shared-models/custom-select/custom-select';
import {environment} from '../../../../../../environments/environment';
import {MicrosoftMailService} from '../../../services/microsoft-mail.service';

@Injectable({
  providedIn: 'root',
})
export class EmailSettingsManager implements OnDestroy {
  protected readonly iconColourEnum = IconColourEnum;
  #sessionDestroy$ = new Subject<void>();
  #stateSubscription: Subscription | undefined;
  readonly #stateSubject = new BehaviorSubject<IEmailSettingsFormState>({
    ...initialState,
  });
  public readonly state$ = this.#stateSubject.asObservable();
  #lastSelectedStoreId: string | null = null;
  #isDirty = false;

  readonly clipboardHasContent = signal<boolean>(false);
  readonly copyFeedback = signal<'idle' | 'copied'>('idle');
  readonly emailAccountChoice = signal<TMailAuthType>(MAIL_AUTH_SMTP);
  readonly microsoftMailConfigured = !!environment.microsoftMail.emailerOrigin;
  #authSnapshot = '';
  #openMicrosoftPanelFor: string | null = null;
  #suppressAccountChoice = false;
  readonly #clipboardKey = 'gallix:email_settings_clipboard';
  #copyFeedbackTimer: ReturnType<typeof setTimeout> | null = null;

  private readonly auth = inject(Auth);
  private readonly microsoftMail = inject(MicrosoftMailService);

  constructor(
    private readonly store: Store,
    private readonly validationManager: EmailValidationManager,
    private readonly configManager: EmailConfigurationManager,
    private readonly modalUtils: SharedModalUtils,
    private readonly collectionUsersSettingsService: CollectionUsersSettingsService
  ) {}

  //#region Public Accessors
  public get currentState(): IEmailSettingsFormState {
    return this.#stateSubject.getValue();
  }

  public get hasValidationErrors(): boolean {
    const settings = this.currentState.selectedColleagueEmailSettings;
    if (!settings || !this.hasRequiredFields(settings)) return true;
    const formErrors = this.currentState.formErrors;
    if (!formErrors) return false;
    const hasTopLevelErrors = ['email', 'user', 'pwd'].some(
      (field: string) => !!formErrors[field]
    );
    if (hasTopLevelErrors) return true;
    if (
      formErrors.smtp &&
      Object.values(formErrors.smtp).some((error: string) => !!error)
    ) {
      return true;
    }
    if (
      settings.enableImap &&
      formErrors.imap &&
      Object.values(formErrors.imap).some((error: string) => !!error)
    ) {
      return true;
    }
    return false;
  }

  public get isDirty(): boolean {
    return this.#isDirty;
  }

  public set isDirty(value: boolean) {
    this.#isDirty = value;
  }

  //#endregion

  //#region Public Methods
  /**
   * Tears down active store subscriptions when the email settings panel is destroyed.
   */
  public teardown(): void {
    if (this.#stateSubscription && !this.#stateSubscription.closed) {
      this.#stateSubscription.unsubscribe();
      this.#stateSubscription = undefined;
    }
    if (!this.#sessionDestroy$.closed) {
      this.#sessionDestroy$.next();
      this.#sessionDestroy$.complete();
    }
    this.#sessionDestroy$ = new Subject<void>();
    this.#authSnapshot = '';
    this.releaseMicrosoftWindow();
    if (this.#copyFeedbackTimer !== null) {
      clearTimeout(this.#copyFeedbackTimer);
      this.#copyFeedbackTimer = null;
    }
  }

  public initialize(): Observable<IEmailSettingsFormState> {
    this.teardown();
    this.clipboardHasContent.set(!!localStorage.getItem(this.#clipboardKey));
    this.store.select(selectColleagues).pipe(take(1)).subscribe((colleagues: IColleagues) => {
      if (!colleagues || Object.keys(colleagues).length === 0) {
        this.store.dispatch(getColleagues());
      }
    });
    this.store.dispatch(settingUserStoreMailerConfiguration.getAllUsersMailerConfigurations());
    this.#stateSubscription = this.store.select(selectEmailSettingsView).pipe(
      takeUntil(this.#sessionDestroy$),
      tap(({ selectedStore }) => {
        const storeChanged = selectedStore?.storeId !== this.currentState.selectedStore?.storeId;
        if (storeChanged) {
          this.store.dispatch(settingUserStoreMailerConfiguration.getAllUsersMailerConfigurations());
        }
      }),
      map(({colleagues, emailSettings, mailerDocumentExists, selectedStore, isSubmitting, isLoadingSettings, canManageColleagueEmails, signedInUserId, microsoftMailBusyLabel}) => {
        const storeChanged = selectedStore?.storeId !== this.currentState.selectedStore?.storeId;
        const currentUserId: string | undefined = signedInUserId || colleagues[0]?.id;
        const previousSelected: string | null = this.currentState.selectedColleague;
        const selectedStillVisible: boolean = !!previousSelected
          && colleagues.some((user: IUser) => user.id === previousSelected);
        const selectedColleague: string | undefined = (!canManageColleagueEmails || storeChanged || !selectedStillVisible)
          ? currentUserId
          : (previousSelected ?? currentUserId);
        const getColleagueSettings = (id: string | null | undefined): IEmailSettings | null => {
          if (!emailSettings || !id) return null;
          return isNonEmptyObject(emailSettings[id]) ? emailSettings[id] : defaultEmailSettings;
        };
        const savedSettings = getColleagueSettings(selectedColleague);
        const keepLocalEdits = this.#isDirty && !storeChanged && selectedStillVisible;
        const selectedColleagueEmailSettings = keepLocalEdits
          ? (this.currentState.selectedColleagueEmailSettings ?? savedSettings)
          : savedSettings;
        return {
          colleagues,
          canManageColleagueEmails,
          colleaguesEmailSettings: emailSettings,
          mailerDocumentExists,
          selectedColleague,
          selectedColleagueEmailSettings,
          selectedStore,
          filteredColleagues: colleagues,
          userQuery: this.currentState.userQuery ?? '',
          formErrors: keepLocalEdits ? (this.currentState.formErrors ?? {}) : {},
          isSubmitting,
          isLoadingSettings,
          signedInUserId: currentUserId,
          microsoftMailBusyLabel,
        } as IEmailSettingsFormState;
      }),
    ).subscribe((state: IEmailSettingsFormState) => {
      this.currentState ?
        this.#stateSubject.next({ ...this.currentState, ...state }) :
        this.#stateSubject.next(state);
      this.#syncEmailAccountChoice(this.currentState);
    });
    this.#isDirty = false;
    return this.state$;
  }

  public async onSelectColleague(id: string): Promise<void> {
    if (!this.currentState.canManageColleagueEmails) {
      return;
    }
    const canSwitch = await this.tryDiscardUnsavedChanges([
      'Do you want to discard your changes and select a different user?',
    ]);
    if (!canSwitch) return;
    this.releaseMicrosoftWindow();
    this.#suppressAccountChoice = true;
    this.#isDirty = false;
    this.updateState({
      selectedColleagueEmailSettings:
        this.getSelectedColleagueEmailSettings(id),
      selectedColleague: id,
      formErrors: {},
    });
    this.#syncEmailAccountChoice(this.currentState);
    setTimeout((): void => {
      this.#suppressAccountChoice = false;
    });
  }

  public onQueryChange(query: string): void {
    const nextQuery = query ?? '';
    const filteredColleagues = this.filterColleagues(nextQuery);
    this.updateState({
      userQuery: nextQuery,
      filteredColleagues,
    });
  }

  public onEmailChange(event: Event): void {
    const email = (event.target as HTMLInputElement).value;
    const currentSettings = this.currentState.selectedColleagueEmailSettings;
    if (!currentSettings) return;
    const error = this.validationManager.isValidEmail(email);
    this.updateFormError('email', error);
    this.updateSelectedColleagueSettings({ ...currentSettings, email });
  }

  public onUserChange(event: Event): void {
    const user = (event.target as HTMLInputElement).value;
    const currentSettings = this.currentState.selectedColleagueEmailSettings;
    if (!currentSettings) return;
    const error = this.validationManager.isValidUser(user);
    this.updateFormError('user', error);
    this.updateSelectedColleagueSettings({ ...currentSettings, user });
  }

  public onPasswordChange(event: Event): void {
    const pwd = (event.target as HTMLInputElement).value;
    const currentSettings = this.currentState.selectedColleagueEmailSettings;
    if (!currentSettings) return;
    const error = this.validationManager.isValidPassword(pwd);
    this.updateFormError('pwd', error);
    this.updateSelectedColleagueSettings({
      ...currentSettings,
      pwd,
      encryptedPwd: false,
    });
  }

  public onSmtpServerChange(event: Event): void {
    const server = (event.target as HTMLInputElement).value;
    const currentSettings = this.currentState.selectedColleagueEmailSettings;
    if (!currentSettings) return;
    const error = this.validationManager.isValidSmtpServer(server);
    this.updateServerError('smtp', 'server', error);
    const updatedSettings = this.configManager.updateSmtpConfig(
      currentSettings,
      { server }
    );
    this.updateSelectedColleagueSettings(updatedSettings);
  }

  public onSmtpPortChange(event: Event): void {
    const numPort = parseInt((event.target as HTMLInputElement).value, 10) || 0;
    const currentSettings = this.currentState.selectedColleagueEmailSettings;
    if (!currentSettings) return;
    const error = this.validationManager.isValidSmtpPort(numPort);
    this.updateServerError('smtp', 'port', error);
    const updatedSettings = this.configManager.updateSmtpConfig(
      currentSettings,
      { port: numPort }
    );
    this.updateSelectedColleagueSettings(updatedSettings);
  }

  public onSmtpSslOnlyChange(checked: boolean): void {
    const currentSettings = this.currentState.selectedColleagueEmailSettings;
    if (!currentSettings) return;
    const updatedSettings = this.configManager.updateSmtpConfig(
      currentSettings,
      {
        sslOnly: checked,
      }
    );
    this.updateSelectedColleagueSettings(updatedSettings);
  }

  public onEnableImapChange(checked: boolean): void {
    const currentSettings = this.currentState.selectedColleagueEmailSettings;
    if (!currentSettings) return;
    const updatedSettings = this.configManager.updateImapEnabled(
      currentSettings,
      checked
    );
    if (!checked) {
      this.updateServerError('imap', 'server', null);
      this.updateServerError('imap', 'port', null);
    }
    this.updateSelectedColleagueSettings(updatedSettings);
  }

  public onImapServerChange(event: Event): void {
    const server = (event.target as HTMLInputElement).value;
    const currentSettings = this.currentState.selectedColleagueEmailSettings;
    if (!currentSettings) return;
    const error = this.validationManager.isValidImapServer(server);
    this.updateServerError('imap', 'server', error);
    const updatedSettings = this.configManager.updateImapConfig(
      currentSettings,
      { server }
    );
    this.updateSelectedColleagueSettings(updatedSettings);
  }

  public onImapPortChange(event: Event): void {
    const numPort = parseInt((event.target as HTMLInputElement).value, 10) || 0;
    const currentSettings = this.currentState.selectedColleagueEmailSettings;
    if (!currentSettings) return;
    const error = this.validationManager.isValidImapPort(numPort);
    this.updateServerError('imap', 'port', error);
    const updatedSettings = this.configManager.updateImapConfig(
      currentSettings,
      { port: numPort }
    );
    this.updateSelectedColleagueSettings(updatedSettings);
  }

  public onImapSslOnlyChange(checked: boolean): void {
    const currentSettings = this.currentState.selectedColleagueEmailSettings;
    if (!currentSettings) return;
    const updatedSettings = this.configManager.updateImapConfig(
      currentSettings,
      {
        sslOnly: checked,
      }
    );
    this.updateSelectedColleagueSettings(updatedSettings);
  }

  /**
   * Switches the email account panel.
   * Standard is saved only when that user chooses it.
   * Microsoft 365 can be opened before any Standard fields exist, and is saved only for that user's own mailbox.
   */
  public onEmailAccountChange(selected: Record<string, ISelectOption>): void {
    if (this.#suppressAccountChoice) {
      return;
    }
    const key = Object.keys(selected).find((optionKey: string) => selected[optionKey].selected);
    if (key !== MAIL_AUTH_SMTP && key !== MAIL_AUTH_MICROSOFT_365) {
      return;
    }
    if (key === this.emailAccountChoice()) {
      return;
    }
    const settings = this.currentState.selectedColleagueEmailSettings;
    if (key === MAIL_AUTH_MICROSOFT_365) {
      this.emailAccountChoice.set(MAIL_AUTH_MICROSOFT_365);
      if (this.selectedUserOwnsMicrosoftConnection()) {
        this.#openMicrosoftPanelFor = null;
        if (settings) {
          this.setSelectedColleagueSettings({...settings, authType: MAIL_AUTH_MICROSOFT_365}, false);
        }
        if (this.hasPersistedMailerDocument()) {
          this.saveAuthType(MAIL_AUTH_MICROSOFT_365);
        }
      } else {
        this.#openMicrosoftPanelFor = this.currentState.selectedColleague;
      }
      return;
    }
    this.#openMicrosoftPanelFor = null;
    this.emailAccountChoice.set(MAIL_AUTH_SMTP);
    if (!settings) {
      return;
    }
    if (shouldPersistStandardAuthType(settings, this.hasPersistedMailerDocument())) {
      this.setSelectedColleagueSettings({...settings, authType: MAIL_AUTH_SMTP}, false);
      this.saveAuthType(MAIL_AUTH_SMTP);
      return;
    }
    this.setSelectedColleagueSettings(applyDisconnectedAccount(settings), false);
  }

  /**
   * True when the selected user's mailer document has its own mailbox sign-in.
   */
  public selectedUserOwnsMicrosoftConnection(): boolean {
    return this.currentState.selectedColleagueEmailSettings?.hasMicrosoftRefreshToken === true;
  }

  /**
   * Opens Microsoft sign-in for the selected user's mailbox.
   */
  public connectMicrosoft365(): void {
    if (!this.canUseMicrosoftAccount()) {
      return;
    }
    const email = this.currentState.selectedColleagueEmailSettings?.email;
    this.store.dispatch(settingUserStoreMailerConfiguration.connectMicrosoftMail({
      loginHint: email || undefined,
      targetUserId: this.currentState.selectedColleague,
    }));
  }

  /**
   * Asks a Microsoft 365 admin to approve Gallix for the organisation.
   */
  public requestMicrosoftAdminConsent(): void {
    if (!this.canUseMicrosoftAccount()) {
      return;
    }
    this.store.dispatch(settingUserStoreMailerConfiguration.requestMicrosoftAdminConsent());
  }

  /**
   * Removes the Microsoft 365 connection for the selected user.
   */
  public disconnectMicrosoft365(): void {
    if (!this.canUseMicrosoftAccount()) {
      return;
    }
    this.store.dispatch(settingUserStoreMailerConfiguration.disconnectMicrosoftMail({
      targetUserId: this.currentState.selectedColleague,
    }));
  }

  /**
   * True when connect, disconnect, and admin approval can run for the selected user.
   */
  public canUseMicrosoftAccount(): boolean {
    const state = this.currentState;
    return this.microsoftMailConfigured
      && !state.microsoftMailBusyLabel
      && !!state.signedInUserId
      && !!state.selectedColleague
      && !!state.selectedStore?.storeId;
  }

  public async saveAndTestSettings(): Promise<void> {
    if (this.emailAccountChoice() === MAIL_AUTH_MICROSOFT_365) {
      await this.saveMicrosoftAccountAndTest();
      return;
    }
    if (this.hasValidationErrors) {
      return;
    }
    try {
      const currentState = this.currentState;
      if (!currentState.selectedColleagueEmailSettings) return;
      if (
        !currentState.selectedColleague ||
        !currentState.selectedStore?.storeId
      ) {
        throw new Error('No selected colleague or store');
      }
      const current = currentState.selectedColleagueEmailSettings;
      const settings = toPersistableMailerSettings({
        ...current,
        authType: MAIL_AUTH_SMTP,
      });
      if (current.encryptedPwd && !current.pwd) {
        delete settings.pwd;
        delete settings.encryptedPwd;
      }
      const path = path_users_userId_settings_stores_storeId_mailer_settings(
        currentState.selectedColleague,
        currentState.selectedStore.storeId
      );
      const updatedSettings = { ...settings, status: 'PENDING' };
      await this.collectionUsersSettingsService.setDocument(
        path,
        updatedSettings
      );
      this.store.dispatch(
        settingUserStoreMailerConfiguration.testMailerConfiguration({
          authType: MAIL_AUTH_SMTP,
          storeId: currentState.selectedStore.storeId,
          userId: currentState.selectedColleague,
        })
      );
      this.store.dispatch(settingUserStoreMailerConfiguration.getAllUsersMailerConfigurations());
      this.#isDirty = false;
    } catch (error) {
      console.error('Failed to save or test email settings:', error);
      const globalError : IGlobalError = {
        name: ErrorName.ActionFailedError,
        message: 'Failed to save or test email settings.',
        stack: error instanceof Error ? error.stack : undefined,
        originalError: error,
      }
      this.store.dispatch(setGlobalErrorOccurrence({ error: globalError }));
    }
  }

  public copySettings(): void {
    const settings = this.currentState.selectedColleagueEmailSettings;
    if (!settings) return;

    const payload: IEmailSettings = {
      email: settings.email,
      user: settings.user ?? '',
      smtp: {...settings.smtp},
      imap: settings.imap ? {...settings.imap} : {port: 993, sentBox: '', server: '', sslOnly: true},
      enableImap: settings.enableImap ?? false,
      // Encrypted passwords cannot be reused for another user — copy as empty so the user re-enters it
      pwd: settings.encryptedPwd ? '' : (settings.pwd ?? ''),
      encryptedPwd: false,
    };

    localStorage.setItem(this.#clipboardKey, JSON.stringify(payload));
    this.clipboardHasContent.set(true);

    if (this.#copyFeedbackTimer !== null) clearTimeout(this.#copyFeedbackTimer);
    this.copyFeedback.set('copied');
    this.#copyFeedbackTimer = setTimeout(() => {
      this.copyFeedback.set('idle');
      this.#copyFeedbackTimer = null;
    }, 2000);
  }

  public pasteSettings(): void {
    const raw = localStorage.getItem(this.#clipboardKey);
    if (!raw) return;

    let pasted: IEmailSettings;
    try {
      pasted = JSON.parse(raw) as IEmailSettings;
    } catch {
      return;
    }

    const current = this.currentState.selectedColleagueEmailSettings ?? defaultEmailSettings;
    const merged: IEmailSettings = {...current, ...pasted, status: undefined};

    this.#revalidateSettings(merged);
    this.#isDirty = true;
    this.updateState({selectedColleagueEmailSettings: merged});
  }

  ngOnDestroy(): void {
    this.teardown();
  }

  private hasRequiredFields(settings: IEmailSettings): boolean {
    const passwordSaved: boolean = !!settings.pwd || settings.encryptedPwd === true;
    if (!settings.email || !settings.user || !passwordSaved) {
      return false;
    }
    if (!settings.smtp?.server || !settings.smtp?.port) {
      return false;
    }
    return !(
      settings.enableImap &&
      (!settings.imap?.server || !settings.imap?.port)
    );
  }

  /**
   * If the form is dirty, prompts with {@link contextDescription} and clears
   * dirty state when the user accepts. Returns true when navigation may
   * proceed (no edits or user discarded).
   */
  public async tryDiscardUnsavedChanges(contextDescription: string[]): Promise<boolean> {
    if (!this.#isDirty) {
      return true;
    }
    const modalProps: ISharedModalBasic = {
      buttonAccept: true,
      buttonAcceptText: 'Discard Changes',
      buttonClose: true,
      buttonCloseCross: false,
      buttonCloseText: 'Cancel',
      modalTitle: 'Unsaved Changes',
      modalTitleIcon: IconsEnum.infoSlabCircleOutline,
      modalTitleIconColor: this.iconColourEnum.yellow,
      contextHeading: 'You have unsaved changes that will be lost.',
      contextDescription,
      cssClass: ['shared-basic-modal-css'],
    };
    const { data } = await this.modalUtils.createModal(modalProps);
    const accepted: boolean = data?.buttonPressed === 'button-accept';
    if (accepted) {
      this.#isDirty = false;
      this.updateState({ formErrors: {} });
    }
    return accepted;
  }

  //#endregion

  //#region Private Helper Methods
  private getSelectedColleagueEmailSettings(
    colleagueId: IUser['id']
  ): IEmailSettings | null {
    const state = this.currentState;
    if (!state.colleaguesEmailSettings || !colleagueId) return null;
    return isNonEmptyObject(state.colleaguesEmailSettings[colleagueId])
      ? state.colleaguesEmailSettings[colleagueId]
      : defaultEmailSettings;
  }

  private filterColleagues(query: string): IUser[] {
    const state = this.currentState;
    if (!query) return state.colleagues || [];
    const normalizedQuery = query.toLowerCase();
    return (state.colleagues || []).filter((user: IUser) => {
      const userId = user.id?.toLowerCase() || '';
      const userName = user.userName?.toLowerCase() || '';
      return (
        userId.includes(normalizedQuery) || userName.includes(normalizedQuery)
      );
    });
  }

  private updateState(updates: Partial<IEmailSettingsFormState>): void {
    this.#stateSubject.next({
      ...this.#stateSubject.getValue(),
      ...updates,
    });
  }

  private updateFormError(
    field: 'email' | 'user' | 'pwd',
    error: string | null
  ): void {
    const formErrors = { ...(this.currentState.formErrors ?? {}) };
    if (error) {
      formErrors[field] = error;
    } else {
      delete formErrors[field];
    }
    this.updateState({ formErrors });
  }

  private updateServerError(
    type: 'smtp' | 'imap',
    field: 'server' | 'port',
    error: string | null
  ): void {
    const formErrors = { ...(this.currentState.formErrors ?? {}) };
    if (!formErrors[type]) formErrors[type] = {};
    if (error) formErrors[type][field] = error;
    else {
      delete formErrors[type][field];
      if (Object.keys(formErrors[type]).length === 0) {
        delete formErrors[type];
      }
    }
    this.updateState({ formErrors });
  }

  /**
   * Saves Microsoft 365 as the sender and runs the existing test.
   * SMTP fields and the Microsoft token are left as they are.
   */
  private async saveMicrosoftAccountAndTest(): Promise<void> {
    try {
      const currentState = this.currentState;
      const settings = currentState.selectedColleagueEmailSettings;
      if (!settings || !currentState.selectedColleague || !currentState.selectedStore?.storeId) {
        throw new Error('No selected colleague or store');
      }
      const mailbox = microsoftMailboxAddress(settings).trim();
      const connected = this.selectedUserOwnsMicrosoftConnection() && mailbox.length > 0;
      if (!connected) {
        return;
      }
      const colleaguePath = path_users_userId_settings_stores_storeId_mailer_settings(
        currentState.selectedColleague,
        currentState.selectedStore.storeId,
      );
      const firebaseUid = this.auth.currentUser?.uid;
      const isSignedInUser = currentState.selectedColleague === currentState.signedInUserId;
      await this.collectionUsersSettingsService.setDocument(colleaguePath, {
        authType: MAIL_AUTH_MICROSOFT_365,
        ...(isSignedInUser && firebaseUid ? {microsoftUserId: firebaseUid} : {}),
      });
      this.updateSelectedColleagueSettings({
        ...settings,
        authType: MAIL_AUTH_MICROSOFT_365,
        ...(isSignedInUser && firebaseUid ? {microsoftUserId: firebaseUid} : {}),
      });
      this.#isDirty = false;
      await this.collectionUsersSettingsService.setDocument(colleaguePath, {
        status: 'PENDING',
      });
      this.store.dispatch(
        settingUserStoreMailerConfiguration.testMailerConfiguration({
          authType: MAIL_AUTH_MICROSOFT_365,
          storeId: currentState.selectedStore.storeId,
          userId: currentState.selectedColleague,
        }),
      );
      this.store.dispatch(settingUserStoreMailerConfiguration.getAllUsersMailerConfigurations());
    } catch (error) {
      console.error('Failed to save or test email settings:', error);
      const globalError: IGlobalError = {
        name: ErrorName.ActionFailedError,
        message: 'Failed to save or test email settings.',
        stack: error instanceof Error ? error.stack : undefined,
        originalError: error,
      };
      this.store.dispatch(setGlobalErrorOccurrence({error: globalError}));
    }
  }

  /**
   * Closes a Microsoft window and clears its status so another user is not left waiting.
   */
  private releaseMicrosoftWindow(): void {
    this.microsoftMail.dismissPopup();
    if (this.currentState.microsoftMailBusyLabel) {
      this.store.dispatch(settingUserStoreMailerConfiguration.microsoftMailCancelled());
    }
  }

  /**
   * Merges authType only. SMTP and IMAP fields stay as they are.
   */
  private saveAuthType(authType: TMailAuthType): void {
    const state = this.currentState;
    if (!state.selectedColleague || !state.selectedStore?.storeId || state.microsoftMailBusyLabel) {
      return;
    }
    this.store.dispatch(settingUserStoreMailerConfiguration.setMailerAuthType({
      authType,
      userId: state.selectedColleague,
      storeId: state.selectedStore.storeId,
    }));
  }

  /**
   * Follows the saved account when the server settings change and the form is clean.
   */
  #syncEmailAccountChoice(state: IEmailSettingsFormState): void {
    const settings = state.selectedColleagueEmailSettings;
    const key = [
      state.selectedStore?.storeId ?? '',
      state.selectedColleague ?? '',
      settings?.authType ?? '',
      settings?.email ?? '',
      settings?.connectedMailbox ?? '',
      settings?.hasMicrosoftRefreshToken ? '1' : '0',
      settings?.microsoftUserId ?? '',
    ].join('|');
    if (key === this.#authSnapshot) {
      return;
    }
    this.#authSnapshot = key;
    if (this.#isDirty) {
      return;
    }
    const savedMicrosoft = isMicrosoft365Auth(settings) && this.selectedUserOwnsMicrosoftConnection();
    if (savedMicrosoft) {
      this.#openMicrosoftPanelFor = null;
      this.emailAccountChoice.set(MAIL_AUTH_MICROSOFT_365);
      return;
    }
    if (this.#openMicrosoftPanelFor && this.#openMicrosoftPanelFor === state.selectedColleague) {
      this.emailAccountChoice.set(MAIL_AUTH_MICROSOFT_365);
      return;
    }
    this.emailAccountChoice.set(MAIL_AUTH_SMTP);
  }

  private updateSelectedColleagueSettings(settings: IEmailSettings): void {
    this.setSelectedColleagueSettings(settings, true);
  }

  private setSelectedColleagueSettings(settings: IEmailSettings, markDirty: boolean): void {
    this.#isDirty = markDirty;
    this.updateState({
      selectedColleagueEmailSettings: settings,
    });
  }

  private hasPersistedMailerDocument(): boolean {
    const state = this.currentState;
    const userId = state.selectedColleague;
    if (!userId) {
      return false;
    }
    return state.mailerDocumentExists?.[userId] === true;
  }

  /** Re-runs all field validators after a paste and writes fresh errors into state. */
  #revalidateSettings(settings: IEmailSettings): void {
    const errors: IFormErrors = {};

    const emailErr = this.validationManager.isValidEmail(settings.email);
    if (emailErr) errors.email = emailErr;
    const userErr = this.validationManager.isValidUser(settings.user ?? '');
    if (userErr) errors.user = userErr;
    const pwdErr = this.validationManager.isValidPassword(settings.pwd ?? '');
    if (pwdErr) errors.pwd = pwdErr;

    const smtpServerErr = this.validationManager.isValidSmtpServer(settings.smtp?.server ?? '');
    const smtpPortErr = this.validationManager.isValidSmtpPort(settings.smtp?.port ?? 0);
    if (smtpServerErr || smtpPortErr) {
      errors.smtp = {};
      if (smtpServerErr) errors.smtp.server = smtpServerErr;
      if (smtpPortErr) errors.smtp.port = smtpPortErr;
    }

    if (settings.enableImap) {
      const imapServerErr = this.validationManager.isValidImapServer(settings.imap?.server ?? '');
      const imapPortErr = this.validationManager.isValidImapPort(settings.imap?.port ?? 0);
      if (imapServerErr || imapPortErr) {
        errors.imap = {};
        if (imapServerErr) errors.imap.server = imapServerErr;
        if (imapPortErr) errors.imap.port = imapPortErr;
      }
    }

    this.updateState({formErrors: errors});
  }

  //#endregion
}
